import { useState, useEffect, useRef } from "react";
import { Globe, FileText, Plus, RefreshCw, Trash2, Calendar, Link as LinkIcon, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronRight, Clock, Loader2, Search, Sparkles, Eye, X, Code, FileCode, Upload, File, FileType, BookOpen } from "lucide-react";
import KnowledgeTraining from "./KnowledgeTraining";
import BlogLinksManager from "./BlogLinksManager";
import api, { tokenManager } from "../utils/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Switch } from "./ui/switch";
import { logger } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KnowledgeHub = ({ agent }) => {
  const [activeCategory, setActiveCategory] = useState("websites");

  const handleCategoryChange = (category) => {
    if (category === activeCategory) return;
    setShowAddWebsite(false);
    setShowAddArticle(false);
    setShowUploadDocument(false);
    setActiveCategory(category);
  };
  const [scrapedPages, setScrapedPages] = useState([]);
  const [showAddArticle, setShowAddArticle] = useState(false);
  const [showAddWebsite, setShowAddWebsite] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newArticle, setNewArticle] = useState({ title: "", content: "" });
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [maxPages, setMaxPages] = useState(200);
  const [websites, setWebsites] = useState([]);
  const [expandedWebsites, setExpandedWebsites] = useState({});

  // Scraping progress state
  const [scrapeProgress, setScrapeProgress] = useState(null);
  const [isScraping, setIsScraping] = useState(false);
  const [isReindexing, setIsReindexing] = useState(false);
  const eventSourceRef = useRef(null);
  const lastFetchedCountRef = useRef(0);
  const [urlListPage, setUrlListPage] = useState(1);
  const urlsPerPage = 10;

  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState("rendered"); // "rendered" or "html"

  // Document upload state
  const [documents, setDocuments] = useState([]);
  const [showUploadDocument, setShowUploadDocument] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchScrapedPages();
    fetchConfig();
    fetchWebsites();
    fetchDocuments();
    checkActiveJobs();

    // Cleanup SSE on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Check for active scraping jobs and reconnect
  const checkActiveJobs = async () => {
    try {
      const response = await api.get(`${API}/scrape-jobs`);
      const jobs = response.data || [];

      // Find any job that's still in progress
      const activeJob = jobs.find(job =>
        job.status === "started" ||
        job.status === "discovering" ||
        job.status === "crawling" ||
        job.status === "embedding" ||
        job.status === "indexing" ||
        job.status === "extracting_metadata"
      );

      if (activeJob) {
        // Reconnect to the progress stream
        setIsScraping(true);
        lastFetchedCountRef.current = 0;

        const eventSource = new EventSource(`${API}/scrape-website/progress/${activeJob.job_id}`);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
          try {
            const progress = JSON.parse(event.data);

            // If job not found in active jobs, close the connection
            if (progress.error || progress.message === "Job completed or not found in active jobs") {
              eventSource.close();
              setIsScraping(false);
              setScrapeProgress(null);
              return;
            }

            setScrapeProgress(progress);

            // Refresh pages list periodically during scraping
            if (progress.status === "crawling" && progress.pages_scraped > 0) {
              const currentCount = progress.pages_scraped;
              if (currentCount >= lastFetchedCountRef.current + 5) {
                lastFetchedCountRef.current = currentCount;
                fetchScrapedPages();
                fetchWebsites();
              }
            }

            if (progress.status === "completed") {
              eventSource.close();
              setIsScraping(false);
              fetchScrapedPages();
              fetchWebsites();
              setTimeout(() => {
                setScrapeProgress(null);
              }, 3000);
            } else if (progress.status === "failed") {
              eventSource.close();
              setIsScraping(false);
            }
          } catch (e) {
            logger.error("Error parsing progress:", e);
          }
        };

        eventSource.onerror = () => {
          eventSource.close();
          setIsScraping(false);
          setScrapeProgress(null);
        };
      }
    } catch (error) {
      logger.error("Error checking active jobs:", error);
    }
  };

  const fetchWebsites = async () => {
    try {
      const response = await api.get(`${API}/scraped-websites`);
      setWebsites(response.data || []);
    } catch (error) {
      logger.error("Error fetching websites:", error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await api.get(`${API}/documents`);
      setDocuments(response.data || []);
    } catch (error) {
      logger.error("Error fetching documents:", error);
    }
  };

  const fetchConfig = async () => {
    try {
      // Use tenant-config to get tenant-specific max_pages_to_scrape value
      const response = await api.get(`${API}/tenant-config`);
      setMaxPages(response.data.max_pages_to_scrape || 200);
    } catch (error) {
      logger.error("Error fetching tenant config:", error);
    }
  };

  const fetchScrapedPages = async () => {
    try {
      const response = await api.get(`${API}/scraped-content`);
      if (Array.isArray(response.data)) {
        setScrapedPages(response.data);
      } else {
        setScrapedPages([response.data]);
      }
    } catch (error) {
      logger.error("Error fetching pages:", error);
      setScrapedPages([]);
    }
  };

  const handleScrapeWebsite = async () => {
    if (!websiteUrl.trim()) {
      alert("Please enter a website URL");
      return;
    }

    // Basic validation - just check it looks like a domain
    const urlPattern = /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/.*)?$/i;
    if (!urlPattern.test(websiteUrl.trim())) {
      alert("Please enter a valid URL (e.g., example.com or https://example.com)");
      return;
    }

    setIsScraping(true);
    lastFetchedCountRef.current = 0;
    setScrapeProgress({
      status: "initializing",
      message: "Starting scrape job...",
      pages_found: 0,
      pages_scraped: 0,
      pages_indexed: 0,
      chunks_created: 0,
      current_url: "",
      errors_count: 0
    });

    try {
      // Start the scraping job with max pages from config
      const response = await api.post(`${API}/scrape-website/start`, {
        website_url: websiteUrl,
        max_pages: maxPages
      });

      const jobId = response.data.job_id;

      // Connect to SSE for progress updates
      const eventSource = new EventSource(`${API}/scrape-website/progress/${jobId}`);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const progress = JSON.parse(event.data);
          setScrapeProgress(progress);

          // Refresh pages list periodically during scraping (every 5 new pages)
          if (progress.status === "crawling" && progress.pages_scraped > 0) {
            const currentCount = progress.pages_scraped;
            if (currentCount >= lastFetchedCountRef.current + 5) {
              lastFetchedCountRef.current = currentCount;
              fetchScrapedPages();
              fetchWebsites();
            }
          }

          // Check if completed or failed
          if (progress.status === "completed") {
            eventSource.close();
            setIsScraping(false);
            fetchScrapedPages();
            fetchWebsites();
            setWebsiteUrl("");
            setTimeout(() => {
              setShowAddWebsite(false);
              setScrapeProgress(null);
            }, 3000);
          } else if (progress.status === "failed") {
            eventSource.close();
            setIsScraping(false);
          }
        } catch (e) {
          logger.error("Error parsing progress:", e);
        }
      };

      eventSource.onerror = (error) => {
        logger.error("SSE error:", error);
        eventSource.close();
        setIsScraping(false);
        setScrapeProgress(prev => ({
          ...prev,
          status: "failed",
          message: "Connection lost. Please try again."
        }));
      };

    } catch (error) {
      logger.error("Error starting scrape:", error);
      setIsScraping(false);
      setScrapeProgress({
        status: "failed",
        message: error.response?.data?.detail || "Error starting scrape job",
        pages_found: 0,
        pages_scraped: 0,
        pages_indexed: 0,
        chunks_created: 0,
        errors_count: 1
      });
    }
  };

  const handleCancelScrape = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    setIsScraping(false);
    setScrapeProgress(null);
  };

  const handleAddArticle = async () => {
    if (!newArticle.title || !newArticle.content) {
      alert("Please provide both title and content");
      return;
    }

    setIsLoading(true);
    try {
      await api.post(`${API}/scraped-content/add`, {
        url: `article://${Date.now()}`,
        title: newArticle.title,
        content: newArticle.content,
        source: "manual_article"
      });

      alert("Article added successfully!");
      await fetchScrapedPages();
      setNewArticle({ title: "", content: "" });
      setShowAddArticle(false);
    } catch (error) {
      logger.error("Error adding article:", error);
      alert("Error adding article. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePage = async (url) => {
    if (!window.confirm("Are you sure you want to delete this content?")) return;

    try {
      await api.post(`${API}/scraped-content/delete`, { url });
      await fetchScrapedPages();
      await fetchWebsites();
    } catch (error) {
      logger.error("Error deleting content:", error);
      alert("Error deleting content");
    }
  };

  const handleToggleActive = async (url, currentActive) => {
    try {
      await api.post(`${API}/scraped-content/toggle`, {
        url,
        is_active: !currentActive
      });
      // Update local state
      setScrapedPages(pages =>
        pages.map(p =>
          p.url === url ? { ...p, is_active: !currentActive } : p
        )
      );
      await fetchWebsites();
    } catch (error) {
      logger.error("Error toggling content:", error);
      alert("Error updating content status");
    }
  };

  const handleReindex = async () => {
    if (!window.confirm("This will rebuild the AI knowledge index with improved settings. Continue?")) {
      return;
    }

    setIsReindexing(true);
    try {
      const response = await api.post(`${API}/reindex`, {}, { timeout: 600000 }); // 10 min timeout for large indexes
      alert(`Reindexing complete!\n\nPages indexed: ${response.data.pages_indexed}\nChunks created: ${response.data.chunks_created}`);
    } catch (error) {
      logger.error("Error reindexing:", error);
      alert("Error reindexing content. Please try again.");
    } finally {
      setIsReindexing(false);
    }
  };

  // Document upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await handleFileUpload(Array.from(files));
    }
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(Array.from(files));
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileUpload = async (files) => {
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadProgress({
      total: files.length,
      completed: 0,
      current: files[0].name,
      results: []
    });

    const results = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(prev => ({
        ...prev,
        current: file.name,
        completed: i
      }));

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await api.post(`${API}/documents/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });

        results.push({
          filename: file.name,
          success: true,
          ...response.data.document
        });
      } catch (error) {
        results.push({
          filename: file.name,
          success: false,
          error: error.response?.data?.detail || error.message
        });
      }
    }

    setUploadProgress(prev => ({
      ...prev,
      completed: files.length,
      results
    }));

    // Refresh documents list
    await fetchDocuments();

    // Clear progress after 3 seconds if all successful
    const allSuccessful = results.every(r => r.success);
    if (allSuccessful) {
      setTimeout(() => {
        setUploadProgress(null);
        setShowUploadDocument(false);
      }, 2000);
    }

    setIsUploading(false);
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;

    try {
      await api.delete(`${API}/documents/${docId}`);
      await fetchDocuments();
    } catch (error) {
      logger.error("Error deleting document:", error);
      alert("Error deleting document");
    }
  };

  const handleDeleteAllDocuments = async () => {
    if (!window.confirm(`Are you sure you want to delete all ${documents.length} documents? This cannot be undone.`)) return;

    try {
      const response = await api.delete(`${API}/documents`);
      alert(`Deleted ${response.data.deleted_count} documents`);
      await fetchDocuments();
    } catch (error) {
      logger.error("Error deleting all documents:", error);
      alert("Error deleting documents");
    }
  };

  const handleToggleDocumentActive = async (docId, currentActive) => {
    try {
      await api.put(`${API}/documents/${docId}`, {
        is_active: !currentActive
      });
      // Update local state
      setDocuments(docs =>
        docs.map(d =>
          d.id === docId ? { ...d, is_active: !currentActive } : d
        )
      );
    } catch (error) {
      logger.error("Error toggling document:", error);
      alert("Error updating document status");
    }
  };

  const getFileTypeIcon = (fileType) => {
    switch (fileType) {
      case "pdf":
        return <FileType className="w-5 h-5 text-red-500" />;
      case "docx":
        return <FileText className="w-5 h-5 text-blue-500" />;
      case "txt":
      case "markdown":
        return <FileCode className="w-5 h-5 text-gray-500" />;
      case "csv":
        return <File className="w-5 h-5 text-green-500" />;
      default:
        return <File className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleDeleteWebsite = async (sourceUrl, e) => {
    e.stopPropagation(); // Prevent expanding/collapsing
    if (!window.confirm(`Delete all pages from ${sourceUrl}? This cannot be undone.`)) {
      return;
    }

    try {
      await api.post(`${API}/scraped-website/delete`, { source_url: sourceUrl });
      await fetchScrapedPages();
      await fetchWebsites();
      alert("Website deleted successfully");
    } catch (error) {
      logger.error("Error deleting website:", error);
      alert("Error deleting website. Please try again.");
    }
  };

  const handleRescrapeWebsite = async (sourceUrl, e) => {
    e.stopPropagation(); // Prevent expanding/collapsing
    if (!window.confirm(`Re-scrape ${sourceUrl}? This will replace all existing pages from this website.`)) {
      return;
    }

    // Use the source URL for re-scraping
    setWebsiteUrl(sourceUrl);
    setIsScraping(true);
    lastFetchedCountRef.current = 0;
    setScrapeProgress({
      status: "initializing",
      message: "Starting re-scrape job...",
      pages_found: 0,
      pages_scraped: 0,
      pages_indexed: 0,
      chunks_created: 0,
      current_url: "",
      errors_count: 0
    });

    try {
      // Start the scraping job
      const response = await api.post(`${API}/scrape-website/start`, {
        website_url: sourceUrl,
        max_pages: maxPages
      });

      const jobId = response.data.job_id;

      // Connect to SSE for progress updates
      const eventSource = new EventSource(`${API}/scrape-website/progress/${jobId}`);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const progress = JSON.parse(event.data);
          setScrapeProgress(progress);

          // Refresh pages list periodically during scraping (every 5 new pages)
          if (progress.status === "crawling" && progress.pages_scraped > 0) {
            const currentCount = progress.pages_scraped;
            if (currentCount >= lastFetchedCountRef.current + 5) {
              lastFetchedCountRef.current = currentCount;
              fetchScrapedPages();
              fetchWebsites();
            }
          }

          // Check if completed or failed
          if (progress.status === "completed") {
            eventSource.close();
            setIsScraping(false);
            fetchScrapedPages();
            fetchWebsites();
            setWebsiteUrl("");
            setTimeout(() => {
              setScrapeProgress(null);
            }, 3000);
          } else if (progress.status === "failed") {
            eventSource.close();
            setIsScraping(false);
          }
        } catch (err) {
          logger.error("Error parsing progress:", err);
        }
      };

      eventSource.onerror = (error) => {
        logger.error("SSE error:", error);
        eventSource.close();
        setIsScraping(false);
        setScrapeProgress(prev => ({
          ...prev,
          status: "failed",
          message: "Connection lost. Please try again."
        }));
      };

    } catch (error) {
      logger.error("Error starting re-scrape:", error);
      setIsScraping(false);
      setScrapeProgress({
        status: "failed",
        message: error.response?.data?.detail || "Error starting re-scrape job",
        pages_found: 0,
        pages_scraped: 0,
        pages_indexed: 0,
        chunks_created: 0,
        errors_count: 1
      });
    }
  };

  const toggleWebsiteExpand = (sourceUrl) => {
    setExpandedWebsites(prev => ({
      ...prev,
      [sourceUrl]: !prev[sourceUrl]
    }));
  };

  const handlePreview = async (page) => {
    setShowPreview(true);
    setPreviewLoading(true);
    setPreviewMode("rendered");

    try {
      // Use id if available, otherwise use URL encoded
      const contentId = page.id || encodeURIComponent(page.url);
      const response = await api.get(`${API}/scraped-content/${contentId}/preview`);
      setPreviewData(response.data);
    } catch (error) {
      logger.error("Error fetching preview:", error);
      setPreviewData({
        title: page.title || "Untitled",
        url: page.url,
        content: page.content || "No content available",
        html_content: "",
        error: "Could not load HTML preview. Showing extracted text instead."
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    setPreviewMode("rendered");
  };

  const getPagesByWebsite = () => {
    const grouped = {};
    scrapedPages
      .filter(p => !p.source || (p.source !== "manual_article" && p.source !== "uploaded_document"))
      .forEach(page => {
        const sourceUrl = page.source_url || "Unknown";
        if (!grouped[sourceUrl]) {
          grouped[sourceUrl] = [];
        }
        grouped[sourceUrl].push(page);
      });
    return grouped;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressPercentage = () => {
    if (!scrapeProgress) return 0;
    if (scrapeProgress.status === "completed") return 100;
    if (scrapeProgress.status === "discovering") {
      // Discovery phase: 0-20%
      return Math.min(scrapeProgress.pages_found * 0.5, 20);
    }
    if (scrapeProgress.status === "crawling") {
      // Scraping phase: 20-55%
      if (scrapeProgress.pages_found === 0) return 20;
      const scrapeProgress_pct = scrapeProgress.pages_scraped / scrapeProgress.pages_found;
      return 20 + (scrapeProgress_pct * 35);
    }
    if (scrapeProgress.status === "embedding" || scrapeProgress.status === "indexing") {
      // Embedding phase: 55-90%
      const embedProgress = scrapeProgress.pages_indexed / Math.max(scrapeProgress.pages_scraped, 1);
      return 55 + (embedProgress * 35);
    }
    if (scrapeProgress.status === "extracting_metadata") {
      // Metadata extraction phase: 90-99%
      return 95;
    }
    return 5;
  };

  const getStatusIcon = () => {
    if (!scrapeProgress) return null;
    switch (scrapeProgress.status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "failed":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
    }
  };

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r p-4 space-y-2">
        <h2 className="font-bold text-lg mb-4 text-gray-800">Knowledge Hub</h2>

        <button
          onClick={() => handleCategoryChange("websites")}
          className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
            activeCategory === "websites"
              ? "bg-[var(--g6-primary)] text-white"
              : "hover:bg-gray-100 text-gray-700"
          }`}
        >
          <div className="flex items-center space-x-2">
            <Globe className="w-4 h-4" />
            <span>Websites</span>
          </div>
          <Badge variant="secondary" className={activeCategory === "websites" ? "bg-white/20" : ""}>
            {scrapedPages.filter(p => !p.source || (p.source !== "manual_article" && p.source !== "uploaded_document")).length}
          </Badge>
        </button>

        <button
          onClick={() => handleCategoryChange("documents")}
          className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
            activeCategory === "documents"
              ? "bg-[var(--g6-primary)] text-white"
              : "hover:bg-gray-100 text-gray-700"
          }`}
        >
          <div className="flex items-center space-x-2">
            <Upload className="w-4 h-4" />
            <span>Documents</span>
          </div>
          <Badge variant="secondary" className={activeCategory === "documents" ? "bg-white/20" : ""}>
            {documents.length}
          </Badge>
        </button>

        <button
          onClick={() => handleCategoryChange("articles")}
          className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
            activeCategory === "articles"
              ? "bg-[var(--g6-primary)] text-white"
              : "hover:bg-gray-100 text-gray-700"
          }`}
        >
          <div className="flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>Articles</span>
          </div>
          <Badge variant="secondary" className={activeCategory === "articles" ? "bg-white/20" : ""}>
            {scrapedPages.filter(p => p.source === "manual_article").length}
          </Badge>
        </button>

        <button
          onClick={() => handleCategoryChange("blog-links")}
          className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
            activeCategory === "blog-links"
              ? "bg-blue-500 text-white"
              : "hover:bg-blue-50 text-gray-700"
          }`}
        >
          <div className="flex items-center space-x-2">
            <BookOpen className="w-4 h-4" />
            <span>Blog Links</span>
          </div>
          <Badge variant="secondary" className={`${activeCategory === "blog-links" ? "bg-white/20" : "bg-blue-100 text-blue-800"}`}>
            New
          </Badge>
        </button>

        <button
          onClick={() => handleCategoryChange("training")}
          className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
            activeCategory === "training"
              ? "bg-yellow-500 text-white"
              : "hover:bg-yellow-50 text-gray-700"
          }`}
        >
          <div className="flex items-center space-x-2">
            <Sparkles className="w-4 h-4" />
            <span>Training</span>
          </div>
          <Badge variant="secondary" className={`${activeCategory === "training" ? "bg-white/20" : "bg-yellow-100 text-yellow-800"}`}>
            New
          </Badge>
        </button>

        <div className="pt-4 border-t mt-4">
          <p className="text-xs text-gray-500 mb-2">Model Usage</p>
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total Content</span>
              <span className="font-semibold">{scrapedPages.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Used by AI</span>
              <span className="font-semibold text-[var(--g6-primary)]">
                {scrapedPages.filter(p => p.is_active !== false).length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {activeCategory === "websites" ? "Websites" :
                 activeCategory === "documents" ? "Documents" :
                 activeCategory === "articles" ? "Articles" :
                 activeCategory === "blog-links" ? "Blog Links" : "Training"}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {activeCategory === "documents"
                  ? "Upload and manage PDF, Word, and text documents"
                  : activeCategory === "blog-links"
                  ? "Manage blog/resource links for chat responses"
                  : "Manage your knowledge base content"}
              </p>
            </div>
            <div className="flex space-x-2">
              {/* Reindex button - visible on all content tabs */}
              {activeCategory !== "training" && (
                <Button
                  onClick={handleReindex}
                  variant="outline"
                  disabled={isScraping || isReindexing || scrapedPages.length === 0}
                  title="Reindex all content (websites, documents, articles)"
                >
                  {isReindexing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {isReindexing ? "Reindexing..." : "Reindex All"}
                </Button>
              )}
              {activeCategory === "websites" && (
                <Button
                  onClick={() => setShowAddWebsite(!showAddWebsite)}
                  className="bg-[var(--g6-primary)] hover:bg-[#008A44]"
                  disabled={isScraping}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Website
                </Button>
              )}
              {activeCategory === "documents" && (
                <>
                  {documents.length > 0 && (
                    <Button
                      onClick={handleDeleteAllDocuments}
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All
                    </Button>
                  )}
                  <Button
                    onClick={() => setShowUploadDocument(!showUploadDocument)}
                    className="bg-[var(--g6-primary)] hover:bg-[#008A44]"
                    disabled={isUploading}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </>
              )}
              {activeCategory === "articles" && (
                <Button
                  onClick={() => setShowAddArticle(!showAddArticle)}
                  className="bg-[var(--g6-primary)] hover:bg-[#008A44]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Article
                </Button>
              )}
            </div>
          </div>

          {/* Scraping Progress (always visible when scraping) */}
          {(isScraping || scrapeProgress) && (
            <Card className="mb-6 border-blue-200 bg-blue-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center space-x-2">
                  {scrapeProgress?.status === "discovering" ? (
                    <><Search className="w-5 h-5" /> <span>Discovering Pages</span></>
                  ) : scrapeProgress?.status === "crawling" ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> <span>Scraping Pages</span></>
                  ) : scrapeProgress?.status === "embedding" ? (
                    <><RefreshCw className="w-5 h-5 animate-spin" /> <span>Creating Embeddings</span></>
                  ) : scrapeProgress?.status === "indexing" ? (
                    <><RefreshCw className="w-5 h-5 animate-spin" /> <span>Indexing Content</span></>
                  ) : scrapeProgress?.status === "extracting_metadata" ? (
                    <><RefreshCw className="w-5 h-5 animate-spin" /> <span>Extracting Metadata</span></>
                  ) : scrapeProgress?.status === "completed" ? (
                    <><CheckCircle className="w-5 h-5 text-green-500" /> <span>Completed</span></>
                  ) : scrapeProgress?.status === "failed" ? (
                    <><XCircle className="w-5 h-5 text-red-500" /> <span>Failed</span></>
                  ) : (
                    <span>Initializing...</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Status Header */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600">{scrapeProgress?.message}</p>
                    {scrapeProgress?.elapsed_seconds && (
                      <span className="text-sm text-gray-500">
                        {Math.round(scrapeProgress.elapsed_seconds)}s elapsed
                      </span>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <Progress value={getProgressPercentage()} className="h-2" />
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <p className="text-2xl font-bold text-gray-800">{scrapeProgress?.pages_found || 0}</p>
                      <p className="text-xs text-gray-500">Discovered</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <p className="text-2xl font-bold text-blue-600">{scrapeProgress?.pages_scraped || 0}</p>
                      <p className="text-xs text-gray-500">Scraped</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <p className="text-2xl font-bold text-green-600">{scrapeProgress?.pages_indexed || 0}</p>
                      <p className="text-xs text-gray-500">Indexed</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg text-center border">
                      <p className="text-2xl font-bold text-purple-600">{scrapeProgress?.chunks_created || 0}</p>
                      <p className="text-xs text-gray-500">Chunks</p>
                    </div>
                  </div>

                  {/* URL List with Status */}
                  {scrapeProgress?.url_statuses && Object.keys(scrapeProgress.url_statuses).length > 0 && (
                    <div className="border rounded-lg bg-white max-h-64 overflow-y-auto">
                      <div className="sticky top-0 bg-gray-50 px-3 py-2 border-b text-xs font-medium text-gray-500">
                        Page URLs ({Object.keys(scrapeProgress.url_statuses).length})
                      </div>
                      <div className="divide-y">
                        {Object.entries(scrapeProgress.url_statuses)
                          .slice((urlListPage - 1) * urlsPerPage, urlListPage * urlsPerPage)
                          .map(([url, status]) => (
                            <div key={url} className="flex items-center justify-between px-3 py-2 text-sm">
                              <span className="truncate flex-1 mr-2 text-gray-700">{url}</span>
                              <span className="flex-shrink-0">
                                {status === "pending" && (
                                  <span className="flex items-center text-gray-400">
                                    <Clock className="w-3 h-3 mr-1" /> Pending
                                  </span>
                                )}
                                {status === "scraping" && (
                                  <span className="flex items-center text-blue-500">
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Scraping
                                  </span>
                                )}
                                {status === "completed" && (
                                  <span className="flex items-center text-green-500">
                                    <CheckCircle className="w-3 h-3 mr-1" /> Done
                                  </span>
                                )}
                                {status === "indexed" && (
                                  <span className="flex items-center text-purple-500">
                                    <CheckCircle className="w-3 h-3 mr-1" /> Indexed
                                  </span>
                                )}
                                {status === "failed" && (
                                  <span className="flex items-center text-red-500">
                                    <XCircle className="w-3 h-3 mr-1" /> Failed
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                      </div>
                      {/* Pagination */}
                      {Object.keys(scrapeProgress.url_statuses).length > urlsPerPage && (
                        <div className="sticky bottom-0 bg-gray-50 px-3 py-2 border-t flex justify-between items-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setUrlListPage(p => Math.max(1, p - 1))}
                            disabled={urlListPage === 1}
                          >
                            Previous
                          </Button>
                          <span className="text-xs text-gray-500">
                            Page {urlListPage} of {Math.ceil(Object.keys(scrapeProgress.url_statuses).length / urlsPerPage)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setUrlListPage(p => p + 1)}
                            disabled={urlListPage >= Math.ceil(Object.keys(scrapeProgress.url_statuses).length / urlsPerPage)}
                          >
                            Next
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Errors */}
                  {scrapeProgress?.errors_count > 0 && (
                    <div className="flex items-center space-x-1 text-amber-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>{scrapeProgress.errors_count} errors encountered</span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-2 pt-2">
                    {isScraping && (
                      <Button variant="outline" onClick={handleCancelScrape}>
                        Cancel
                      </Button>
                    )}
                    {scrapeProgress?.status === "completed" && (
                      <Button
                        onClick={() => setScrapeProgress(null)}
                        className="bg-[var(--g6-primary)]"
                      >
                        Done
                      </Button>
                    )}
                    {scrapeProgress?.status === "failed" && (
                      <>
                        <Button
                          onClick={() => {
                            setScrapeProgress(null);
                            setShowAddWebsite(true);
                          }}
                          className="bg-[var(--g6-primary)]"
                        >
                          Try Again
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setScrapeProgress(null)}
                        >
                          Close
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add Website Form */}
          {activeCategory === "websites" && showAddWebsite && !isScraping && !scrapeProgress && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Scrape New Website</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Website URL</label>
                    <Input
                      type="text"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="example.com or https://example.com"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button onClick={handleScrapeWebsite} className="bg-[var(--g6-primary)]">
                      <Globe className="w-4 h-4 mr-2" />
                      Start Scraping
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddWebsite(false)}>
                      Cancel
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Will crawl up to {maxPages} pages recursively from the website
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Add Article Form */}
          {activeCategory === "articles" && showAddArticle && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Add New Article</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Title</label>
                    <Input
                      value={newArticle.title}
                      onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
                      placeholder="Article title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Content</label>
                    <Textarea
                      value={newArticle.content}
                      onChange={(e) => setNewArticle({ ...newArticle, content: e.target.value })}
                      placeholder="Article content..."
                      rows={8}
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button onClick={handleAddArticle} disabled={isLoading} className="bg-[var(--g6-primary)]">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Article
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddArticle(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Document Upload Form */}
          {showUploadDocument && activeCategory === "documents" && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Upload Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Drag and Drop Zone */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      dragActive
                        ? "border-[var(--g6-primary)] bg-green-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.docx,.doc,.txt,.md,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-lg font-medium text-gray-700 mb-2">
                      Drag and drop files here
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      or click to browse
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      Select Files
                    </Button>
                    <p className="text-xs text-gray-400 mt-4">
                      Supported formats: PDF, DOCX, TXT, MD, CSV (max 10MB per file)
                    </p>
                  </div>

                  {/* Upload Progress */}
                  {uploadProgress && (
                    <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {isUploading ? `Uploading: ${uploadProgress.current}` : "Upload Complete"}
                        </span>
                        <span className="text-sm text-gray-500">
                          {uploadProgress.completed} / {uploadProgress.total}
                        </span>
                      </div>
                      <Progress
                        value={(uploadProgress.completed / uploadProgress.total) * 100}
                        className="h-2"
                      />

                      {/* Results */}
                      {uploadProgress.results && uploadProgress.results.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {uploadProgress.results.map((result, idx) => (
                            <div
                              key={idx}
                              className={`flex items-center justify-between p-2 rounded text-sm ${
                                result.success ? "bg-green-50" : "bg-red-50"
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                {result.success ? (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-500" />
                                )}
                                {result.filename}
                              </span>
                              {result.success ? (
                                <span className="text-green-600">{result.chunks_indexed} chunks</span>
                              ) : (
                                <span className="text-red-600 text-xs">{result.error}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowUploadDocument(false);
                        setUploadProgress(null);
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Content Display */}
          {activeCategory === "websites" ? (
            /* Grouped Website View */
            <div className="space-y-4">
              {Object.entries(getPagesByWebsite()).map(([sourceUrl, pages]) => (
                <div key={sourceUrl} className="bg-white rounded-lg border">
                  {/* Website Header */}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleWebsiteExpand(sourceUrl)}
                  >
                    <div className="flex items-center space-x-3">
                      {expandedWebsites[sourceUrl] ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                      <Globe className="w-5 h-5 text-[var(--g6-primary)]" />
                      <div>
                        <p className="font-medium text-gray-900">{sourceUrl}</p>
                        <p className="text-xs text-gray-500">
                          {pages.length} pages • {pages.filter(p => p.is_active !== false).length} active
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary">
                        {pages.filter(p => p.is_active !== false).length}/{pages.length}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleRescrapeWebsite(sourceUrl, e)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        disabled={isScraping}
                        title="Re-scrape website"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteWebsite(sourceUrl, e)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete website"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Pages List */}
                  {expandedWebsites[sourceUrl] && (
                    <div className="border-t">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left p-3 text-xs font-medium text-gray-500">Page</th>
                            <th className="text-left p-3 text-xs font-medium text-gray-500">Scraped</th>
                            <th className="text-center p-3 text-xs font-medium text-gray-500">Use in AI</th>
                            <th className="text-center p-3 text-xs font-medium text-gray-500">Preview</th>
                            <th className="text-right p-3 text-xs font-medium text-gray-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pages.map((page, idx) => (
                            <tr key={idx} className="border-t hover:bg-gray-50">
                              <td className="p-3">
                                <p className="text-sm font-medium text-gray-900 line-clamp-1">
                                  {page.title || "Untitled"}
                                </p>
                                <p className="text-xs text-gray-500 line-clamp-1">{page.url}</p>
                              </td>
                              <td className="p-3 text-xs text-gray-500">
                                {formatDate(page.scraped_at)}
                              </td>
                              <td className="p-3 text-center">
                                <Switch
                                  checked={page.is_active !== false}
                                  onCheckedChange={() => handleToggleActive(page.url, page.is_active !== false)}
                                />
                              </td>
                              <td className="p-3 text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handlePreview(page)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  title="Preview content"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </td>
                              <td className="p-3 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeletePage(page.url)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}

              {Object.keys(getPagesByWebsite()).length === 0 && (
                <div className="bg-white rounded-lg border text-center py-12">
                  <Globe className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No websites scraped yet</p>
                  <p className="text-sm text-gray-400 mt-1">Click "Add Website" to get started</p>
                </div>
              )}
            </div>
          ) : activeCategory === "documents" ? (
            /* Documents List */
            <div className="bg-white rounded-lg border">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-gray-700">Document</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-700">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-700">Size</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-700">Uploaded</th>
                    <th className="text-center p-4 text-sm font-medium text-gray-700">Use in AI</th>
                    <th className="text-right p-4 text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {getFileTypeIcon(doc.file_type)}
                          <div>
                            <p className="font-medium text-gray-900 line-clamp-1">
                              {doc.title || "Untitled"}
                            </p>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {doc.original_filename}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="uppercase text-xs">
                          {doc.file_type}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {formatFileSize(doc.file_size)}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(doc.uploaded_at || doc.scraped_at)}</span>
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <Switch
                          checked={doc.is_active !== false}
                          onCheckedChange={() => handleToggleDocumentActive(doc.id, doc.is_active !== false)}
                        />
                      </td>
                      <td className="p-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {documents.length === 0 && (
                <div className="text-center py-12">
                  <Upload className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No documents uploaded yet</p>
                  <p className="text-sm text-gray-400 mt-1">Click "Upload Document" to add PDF, Word, or text files</p>
                </div>
              )}
            </div>
          ) : activeCategory === "articles" ? (
            /* Articles Table */
            <div className="bg-white rounded-lg border">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-gray-700">Title</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-700">Added</th>
                    <th className="text-center p-4 text-sm font-medium text-gray-700">Use in AI</th>
                    <th className="text-center p-4 text-sm font-medium text-gray-700">Preview</th>
                    <th className="text-right p-4 text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scrapedPages
                    .filter(page => page.source === "manual_article")
                    .map((page, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-4">
                          <p className="font-medium text-gray-900 line-clamp-1">
                            {page.title || "Untitled"}
                          </p>
                        </td>
                        <td className="p-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(page.scraped_at)}</span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <Switch
                            checked={page.is_active !== false}
                            onCheckedChange={() => handleToggleActive(page.url, page.is_active !== false)}
                          />
                        </td>
                        <td className="p-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(page)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Preview content"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePage(page.url)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {scrapedPages.filter(p => p.source === "manual_article").length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No articles added yet</p>
                  <p className="text-sm text-gray-400 mt-1">Click "Add Article" to create one</p>
                </div>
              )}
            </div>
          ) : activeCategory === "blog-links" ? (
            /* Blog Links Tab Content */
            <BlogLinksManager agent={agent} />
          ) : activeCategory === "training" ? (
            /* Training Tab Content */
            <KnowledgeTraining agent={agent} />
          ) : null}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            {/* Modal Header */}
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-gray-900 truncate">
                  {previewData?.title || "Loading..."}
                </h3>
                {previewData?.url && (
                  <a
                    href={previewData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline truncate block"
                  >
                    {previewData.url}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 ml-4">
                {/* View Mode Toggle */}
                {previewData?.html_content && (
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setPreviewMode("rendered")}
                      className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition-colors ${
                        previewMode === "rendered"
                          ? "bg-white shadow text-gray-900"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <Eye className="w-4 h-4" />
                      Rendered
                    </button>
                    <button
                      onClick={() => setPreviewMode("html")}
                      className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 transition-colors ${
                        previewMode === "html"
                          ? "bg-white shadow text-gray-900"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <Code className="w-4 h-4" />
                      HTML
                    </button>
                  </div>
                )}
                <button
                  onClick={closePreview}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-4">
              {previewLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading preview...</span>
                </div>
              ) : previewData?.error ? (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  {previewData.error}
                </div>
              ) : null}

              {!previewLoading && previewData && (
                <>
                  {previewMode === "rendered" && previewData.html_content ? (
                    <div className="border rounded-lg bg-white">
                      <iframe
                        srcDoc={previewData.html_content}
                        className="w-full h-[60vh] rounded-lg"
                        title="Content Preview"
                        sandbox="allow-same-origin"
                      />
                    </div>
                  ) : previewMode === "html" && previewData.html_content ? (
                    <div className="border rounded-lg bg-gray-900 p-4 overflow-auto max-h-[60vh]">
                      <pre className="text-sm text-gray-100 whitespace-pre-wrap font-mono">
                        {previewData.html_content}
                      </pre>
                    </div>
                  ) : (
                    <div className="border rounded-lg bg-gray-50 p-4">
                      <div className="flex items-center gap-2 mb-3 text-gray-500">
                        <FileCode className="w-4 h-4" />
                        <span className="text-sm font-medium">Extracted Text Content</span>
                      </div>
                      <div className="prose prose-sm max-w-none">
                        <pre className="whitespace-pre-wrap text-gray-700 font-sans text-sm leading-relaxed">
                          {previewData.content || "No content available"}
                        </pre>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between flex-shrink-0 rounded-b-2xl">
              <div className="text-sm text-gray-500">
                {previewData?.scraped_at && (
                  <span>Scraped: {formatDate(previewData.scraped_at)}</span>
                )}
              </div>
              <Button variant="outline" onClick={closePreview}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeHub;
